import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import Select from 'react-select';
import { SingleDatePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import styledComponent from '../../utils/styledComponent';
import { PackingSlipPreview } from '../../libs/GraphQL';
import { extractPreviewPallets, sortPalletsForDisplay } from './palletUtils';
import PalletSlip from '../PackingSlips/PalletSlip';
import PalletSummaryCard from './PalletSummaryCard';

const SectionHeader = styledComponent('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  '& h4': {
    margin: 0,
    fontWeight: 500,
  },
  '& p': {
    margin: '5px 0 0 0',
    color: '#818387',
    fontSize: '14px',
  },
});

const FilterRow = styledComponent('div', {
  marginBottom: '20px',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '20px',
});

const FilterGroup = styledComponent('div', {
  display: 'inline-block',
  minWidth: '200px',
  '& label': {
    color: '#000',
    fontSize: '18px',
    fontWeight: 800,
    marginBottom: '10px',
    display: 'block',
  },
  '& .SingleDatePickerInput__withBorder': {
    border: 'none',
  },
  '& .SingleDatePicker': {
    display: 'block',
  },
  '& .SingleDatePicker input': {
    background: '#FFF',
    border: '1px solid #CCC',
    borderRadius: '4px',
    fontSize: '16px',
    height: '50px',
    padding: '15px',
    width: '200px',
  },
});

const WarningBanner = styledComponent('div', {
  background: '#fff3cd',
  border: '1px solid #ff9e22',
  color: '#856404',
  padding: '12px 16px',
  borderRadius: '4px',
  marginBottom: '20px',
  fontSize: '14px',
});

const LoadingMessage = styledComponent('div', {
  textAlign: 'center',
  padding: '40px',
  color: '#818387',
  fontSize: '16px',
});

const NoDataMessage = styledComponent('div', {
  textAlign: 'center',
  padding: '40px',
  color: '#818387',
  fontSize: '16px',
});

const selectStyles = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderRadius: '4px',
    border: '1px solid #CCC',
    minHeight: '50px',
    minWidth: '200px',
  }),
};

const PreviewPalletsTab = ({ selectedDate, onDateChange }) => {
  const [isDateFocused, setIsDateFocused] = useState(false);
  const [pallets, setPallets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter states
  const [linehaulFilter, setLinehaulFilter] = useState(null);
  const [middleMileFilter, setMiddleMileFilter] = useState(null);
  const [regionFilter, setRegionFilter] = useState(null);

  // Fetch pallet data when date changes
  useEffect(() => {
    const fetchPallets = async () => {
      if (!selectedDate) return;

      setIsLoading(true);
      setError(null);

      try {
        const formattedDate = selectedDate.format('YYYY-MM-DD');
        const response = await PackingSlipPreview(formattedDate);
        const previewData = response?.data?.packingSlipsPreview || [];
        setPallets(sortPalletsForDisplay(extractPreviewPallets(previewData)));
      } catch (err) {
        console.error('Error fetching pallet preview:', err);
        setError('Failed to load pallet data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPallets();
  }, [selectedDate]);

  // Extract unique filter options from pallets
  const filterOptions = useMemo(() => {
    const linehauls = new Set();
    const middleMiles = new Set();
    const regions = new Set();

    pallets.forEach((pallet) => {
      if (pallet.linehaulFirstMile) linehauls.add(pallet.linehaulFirstMile);
      if (pallet.linehaulMiddleMile) middleMiles.add(pallet.linehaulMiddleMile);
      if (pallet.region) regions.add(pallet.region);
    });

    return {
      linehauls: Array.from(linehauls).map((v) => ({ value: v, label: v })),
      middleMiles: Array.from(middleMiles).map((v) => ({ value: v, label: v })),
      regions: Array.from(regions).map((v) => ({ value: v, label: v })),
    };
  }, [pallets]);

  // Filter pallets based on selected filters
  const filteredPallets = useMemo(() => {
    return pallets.filter((pallet) => {
      if (linehaulFilter && pallet.linehaulFirstMile !== linehaulFilter.value)
        return false;
      if (
        middleMileFilter &&
        pallet.linehaulMiddleMile !== middleMileFilter.value
      )
        return false;
      if (regionFilter && pallet.region !== regionFilter.value) return false;
      return true;
    });
  }, [pallets, linehaulFilter, middleMileFilter, regionFilter]);

  // Group pallets by linehaul
  const palletsByLinehaul = useMemo(() => {
    const grouped = {};
    filteredPallets.forEach((pallet) => {
      const linehaul = pallet.linehaulFirstMile || 'Unknown';
      if (!grouped[linehaul]) {
        grouped[linehaul] = [];
      }
      grouped[linehaul].push(pallet);
    });
    return grouped;
  }, [filteredPallets]);

  // Calculate summary data for each linehaul
  const getLinehaulSummary = (linehaulPallets) => {
    const region = linehaulPallets[0]?.region || 'Unknown';
    const middleMile = linehaulPallets[0]?.linehaulMiddleMile || null;
    const totalPallets = linehaulPallets.length;
    const marketBreakdown = {};

    linehaulPallets.forEach((pallet) => {
      const market = pallet.market || 'Unknown';
      marketBreakdown[market] = (marketBreakdown[market] || 0) + 1;
    });

    return { region, middleMile, totalPallets, marketBreakdown };
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div style={{ paddingTop: '20px' }}>
      <SectionHeader className="no-print">
        <div>
          <h4>Preview Pallets</h4>
          <p>
            Pre-allocation estimates for production planning (~90-95% accurate)
          </p>
        </div>
        <Button onClick={handlePrint}>
          <span className="glyphicon glyphicon-print" /> Print Pallet Slips
        </Button>
      </SectionHeader>

      <FilterRow className="no-print">
        <FilterGroup>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="production-date">Production Date</label>
          <SingleDatePicker
            date={selectedDate}
            isOutsideRange={() => false}
            onDateChange={onDateChange}
            focused={isDateFocused}
            onFocusChange={({ focused }) => setIsDateFocused(focused)}
            id="production-date"
            numberOfMonths={1}
          />
        </FilterGroup>

        <FilterGroup>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="linehaul-filter">Linehaul</label>
          <Select
            inputId="linehaul-filter"
            options={filterOptions.linehauls}
            value={linehaulFilter}
            onChange={setLinehaulFilter}
            isClearable
            placeholder="All"
            styles={selectStyles}
          />
        </FilterGroup>

        <FilterGroup>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="middlemile-filter">Middle Mile</label>
          <Select
            inputId="middlemile-filter"
            options={filterOptions.middleMiles}
            value={middleMileFilter}
            onChange={setMiddleMileFilter}
            isClearable
            placeholder="All"
            styles={selectStyles}
          />
        </FilterGroup>

        <FilterGroup>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="region-filter">Region</label>
          <Select
            inputId="region-filter"
            options={filterOptions.regions}
            value={regionFilter}
            onChange={setRegionFilter}
            isClearable
            placeholder="All"
            styles={selectStyles}
          />
        </FilterGroup>
      </FilterRow>

      {isLoading && <LoadingMessage>Loading pallet data...</LoadingMessage>}

      {error && (
        <WarningBanner>
          <strong>Error:</strong> {error}
        </WarningBanner>
      )}

      {!isLoading && !error && filteredPallets.length === 0 && (
        <NoDataMessage>
          No pallet data available for the selected date and filters.
        </NoDataMessage>
      )}

      {!isLoading && !error && filteredPallets.length > 0 && (
        <>
          <WarningBanner className="no-print">
            <strong>Note:</strong> Preview data is ~90-95% accurate. Run
            allocation for final counts.
          </WarningBanner>

          {Object.entries(palletsByLinehaul).map(
            ([linehaul, linehaulPallets]) => {
              const { region, middleMile, totalPallets, marketBreakdown } =
                getLinehaulSummary(linehaulPallets);

              return (
                <div key={linehaul}>
                  <PalletSummaryCard
                    linehaul={linehaul}
                    region={region}
                    middleMile={middleMile}
                    totalPallets={totalPallets}
                    marketBreakdown={marketBreakdown}
                  />

                  {linehaulPallets.map((pallet, index) => (
                    <PalletSlip
                      key={pallet.uniqueId || index}
                      pallet={pallet}
                      palletIndex={index}
                      totalPallets={totalPallets}
                      showStatus={false}
                    />
                  ))}
                </div>
              );
            },
          )}

          <p className="text-muted no-print" style={{ textAlign: 'center' }}>
            Showing {filteredPallets.length} pallet
            {filteredPallets.length !== 1 ? 's' : ''}.
          </p>
        </>
      )}
    </div>
  );
};

PreviewPalletsTab.propTypes = {
  selectedDate: PropTypes.object,
  onDateChange: PropTypes.func.isRequired,
};

PreviewPalletsTab.defaultProps = {
  selectedDate: null,
};

export default PreviewPalletsTab;
